import { useEffect, useRef, useState } from "react"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import DataTable from "monitor/Tables/DataTable"
import Icon from "@mui/material/Icon"
import { mapConsolidadoToRows } from "layouts/ingresos/data/ingresosTableData"
import { useNavigate } from "react-router-dom"
import Collapse from "@mui/material/Collapse"
import config from "config"
import "flatpickr/dist/themes/dark.css"

import Flatpickr from "react-flatpickr"
import "flatpickr/dist/l10n/es.js"
import MDButton from "components/MDButton"
import ModalSelectorMultiple from "components/ModalSelectorMultiple"
import { useMaterialUIController } from "context"

//jwt decode para verificacion
import { jwtDecode } from "jwt-decode"
import {
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material"
import { showToast } from "utils/swalToast"
import { sanitizeFileName, FILE_NAME_HELP_TEXT } from "utils/fileName"
import { flushSync } from "react-dom"

const PAGE_SIZE = 10

const reindexRows = (rows) =>
  rows.map((row, index) => ({
    ...row,
    item: index + 1,
  }))

const mergeUniqueRows = (prevRows, incomingRows) => {
  const existingIds = new Set(prevRows.map((row) => row.id).filter((id) => id != null))
  const uniqueIncoming = incomingRows.filter((row) => row.id == null || !existingIds.has(row.id))
  return reindexRows([...prevRows, ...uniqueIncoming])
}

// Upsert de una fila consolidada (DTO de permanencia). Empareja por la propia
// visita: misma key, o mismo authEventIngresoId/authEventEgresoId (cuando llega
// el egreso de una visita ya listada con su ingreso).
const upsertConsolidadoRow = (prevRows, dto, highlightNew = false) => {
  const mappedRow = mapConsolidadoToRows([dto], config, 0)[0]
  if (!mappedRow) return { rows: prevRows, isNew: false }

  const existingIndex = prevRows.findIndex((row) => {
    if (row.id === mappedRow.id) return true
    if (dto.authEventIngresoId != null && row.authEventIngresoId === dto.authEventIngresoId) return true
    if (dto.authEventEgresoId != null && row.authEventEgresoId === dto.authEventEgresoId) return true
    return false
  })

  let nextRows = [...prevRows]
  let isNew = false
  if (existingIndex >= 0) {
    mappedRow.__highlight = nextRows[existingIndex]?.__highlight || false
    nextRows[existingIndex] = mappedRow
  } else {
    isNew = true
    mappedRow.__highlight = highlightNew
    nextRows.unshift(mappedRow)
  }

  return { rows: reindexRows(nextRows), isNew }
}

function Ingresos() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  const userPerms = (() => {
    if (!token) return []
    try {
      const decoded = jwtDecode(token)
      const rawPerms = decoded?.perms ?? decoded?.permissions ?? decoded?.authorities
      return Array.isArray(rawPerms)
        ? rawPerms.map((p) => (typeof p === "string" ? p : p?.authority ?? ""))
        : []
    } catch {
      return []
    }
  })()
  const canUseWs = userPerms.includes("WEBSOCKET_INGRESOS")
  const canFilterSegmento = userPerms.includes("SEGMENTO_FILTRAR")
  const SEGMENTO_OPTIONS = [
    { code: "19", descripcion: "Black" },
    { code: "24", descripcion: "Ultra Black" },
  ]
  const [nombreTerm, setNombreTerm] = useState("") // filtro por nombre/persona
  const [docNro, setDocNro] = useState("")
  const [ipEquipo, setIpEquipo] = useState("")
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date()
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [fechaHasta, setFechaHasta] = useState(() => {
    const d = new Date()
    d.setHours(23, 59, 59, 999)
    return d
  })
  const [nextPage, setNextPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [empresas, setEmpresas] = useState([])
  const [sucursales, setSucursales] = useState([])
  const [eventos, setEventos] = useState([])
  const [zonas, setZonas] = useState([])
  const [selectedEmpresas, setSelectedEmpresas] = useState("")
  const [selectedSucursales, setSelectedSucursales] = useState([])
  const [selectedEventos, setSelectedEventos] = useState("")
  const [selectedZonas, setSelectedZonas] = useState("")
  const [selectedTipoDoc, setSelectedTipoDoc] = useState("")
  const [selectedTipoAcceso, setSelectedTipoAcceso] = useState("")
  const [nombreDispositivo, setNombreDispositivo] = useState("")
  const [noIdentificados, setNoIdentificados] = useState(null)
  const [accesosCorrectos, setAccesosCorrectos] = useState(null)
  const [multiplesCoincidencias, setMultiplesCoincidencias] = useState(null)
  const [selectedSegmentos, setSelectedSegmentos] = useState([])
  const [duplicados, setDuplicados] = useState(null)
  const [identifiedCounts, setIdentifiedCounts] = useState(null)

  const [exportFormat, setExportFormat] = useState("XLSX")
  const [isDownloading, setIsDownloading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [pendingFormat, setPendingFormat] = useState("XLSX")
  const [fileName, setFileName] = useState("")

  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null)
  const downloadMenuOpen = Boolean(downloadAnchorEl)
  const openDownloadMenu = (e) => setDownloadAnchorEl(e.currentTarget)
  const closeDownloadMenu = () => setDownloadAnchorEl(null)

  const [filtersOpen, setFiltersOpen] = useState(() => {
    const saved = sessionStorage.getItem("ingresosFiltersOpen")
    return saved ? JSON.parse(saved) : false
  })

  const dialogStyle = {
    backgroundColor: darkMode ? "#1a2035" : "#ffffff",
    color: darkMode ? "#ffffff" : "#1a2035",
  };

  useEffect(() => {
    sessionStorage.setItem("ingresosFiltersOpen", JSON.stringify(filtersOpen))
  }, [filtersOpen])
  const toggleFilters = () => setFiltersOpen((open) => !open)

  const _allColumns = [
    { Header: "Item", accessor: "item", width: "5%", align: "center" },
    { Header: "Persona", accessor: "person", width: "18%", align: "left" },
    { Header: "Sucursal", accessor: "sucursal", width: "10%", align: "center" },
    { Header: "Segmento", accessor: "segmento", width: "8%", align: "center" },
    { Header: "Evento", accessor: "evento", width: "8%", align: "center" },
    { Header: "Entrada", accessor: "entrada", width: "12%", align: "center" },
    { Header: "Salida", accessor: "salida", width: "12%", align: "center" },
    { Header: "Permanencia", accessor: "permanencia", width: "10%", align: "center" },
    { Header: "Detalle", accessor: "detalle", width: "5%", align: "center" },
  ]
  const _filtered = config.ingresosColumns
    ? _allColumns.filter((col) =>
        config.ingresosColumns.split(",").map((s) => s.trim()).includes(col.accessor)
      )
    : _allColumns
  const _envColumns = _filtered.some((c) => c.accessor === "item")
    ? _filtered
    : [_allColumns.find((c) => c.accessor === "item"), ..._filtered]
  const [columns, setColumns] = useState(_envColumns)
  const [rows, setRows] = useState([])
  const navigate = useNavigate()

  const rowsIdsRef = useRef(new Set())
  const highlightTimeoutsRef = useRef({})
  const wsRef = useRef(null)
  const pingRef = useRef(null)
  const reconnectRef = useRef(null)
  const [appliedWsFilters, setAppliedWsFilters] = useState({})
  const [wsEnabled, setWsEnabled] = useState(() => {
    try {
      const saved = sessionStorage.getItem("wsEnabled_ingresos") ?? localStorage.getItem("wsEnabled_ingresos")
      return saved === null ? true : saved === "true"
    } catch {
      return true
    }
  })

  useEffect(() => {
    rowsIdsRef.current = new Set(rows.map((row) => row.id).filter((id) => id != null))
  }, [rows])

  useEffect(() => {
    return () => {
      Object.values(highlightTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId))
      highlightTimeoutsRef.current = {}
    }
  }, [])

  const [authExpired, setAuthExpired] = useState(false)
  useEffect(() => {
    try {
      if (!token) {
        setAuthExpired(true)
        navigate("/authentication/sign-in")
        return
      }
      const decodedToken = jwtDecode(token)
      const currentTime = Date.now() / 1000
      if (decodedToken.exp < currentTime) {
        setAuthExpired(true)
        navigate("/authentication/sign-in")
      }
    } catch (e) {
      setAuthExpired(true)
      navigate("/authentication/sign-in")
    }
  }, [navigate, token])

  useEffect(() => {
    const fetchBackendColumns = async () => {
      try {
        const res = await fetch(`${config.api.baseURL}/columnas/mis-columnas?modulo=INGRESOS`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const data = await res.json()
        if (!Array.isArray(data) || data.length === 0) return
        const mapped = data
          .filter((col) => col.visible)
          .map((col) => {
            const base = _allColumns.find((c) => c.accessor === col.clave)
            return {
              Header: col.nombre,
              accessor: col.clave,
              width: base?.width ?? "10%",
              align: base?.align ?? "center",
            }
          })
        const withItem = mapped.some((c) => c.accessor === "item")
          ? mapped
          : [_allColumns.find((c) => c.accessor === "item"), ...mapped]
        if (withItem.length > 0) setColumns(withItem)
      } catch {
        // mantiene las columnas del .env ya inicializadas en el estado
      }
    }

    const bootstrap = async () => {
      try {
        setIsLoadingData(true)
        await Promise.all([
          getEmpresas(),
          fetchBackendColumns(),
          fetchPageData(0, { append: false }),
        ])
      } finally {
        setIsLoadingData(false)
      }
    }
    bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!token || authExpired) return undefined
    if (!canUseWs || !wsEnabled) return undefined

    const wsProto = window.location.protocol === "https:" ? "wss" : "ws"
    const host = new URL(config.api.baseURL).host
    const clean = (token || "").replace(/\s+/g, "")
    const params = new URLSearchParams({ token: clean })
    if (appliedWsFilters.sucursalIds && appliedWsFilters.sucursalIds.length > 0) {
      appliedWsFilters.sucursalIds.forEach((id) => params.append("sucursalIds", id))
    }
    if (appliedWsFilters.eventoId) params.set("eventoId", appliedWsFilters.eventoId)
    if (appliedWsFilters.zonaId) params.set("zonaId", appliedWsFilters.zonaId)
    if (appliedWsFilters.empresaId) params.set("empresaId", appliedWsFilters.empresaId)
    if (appliedWsFilters.nombre) params.set("nombre", appliedWsFilters.nombre)
    if (appliedWsFilters.tipoDoc) params.set("tipoDoc", appliedWsFilters.tipoDoc)
    if (appliedWsFilters.documentNumber) params.set("documentNumber", appliedWsFilters.documentNumber)
    if (appliedWsFilters.tipoAcceso) params.set("tipoAcceso", appliedWsFilters.tipoAcceso)
    if (appliedWsFilters.noIdentificados !== undefined) params.set("noIdentificados", appliedWsFilters.noIdentificados)
    if (appliedWsFilters.multiplesCoincidencias !== undefined) params.set("multiplesCoincidencias", appliedWsFilters.multiplesCoincidencias)
    if (appliedWsFilters.accesosCorrectos !== undefined) params.set("accesosCorrectos", appliedWsFilters.accesosCorrectos)
    if (appliedWsFilters.segmentos && appliedWsFilters.segmentos.length > 0) {
      appliedWsFilters.segmentos.forEach((s) => params.append("segmentos", s))
    }
    const wsUrl = `${wsProto}://${host}/ws/authentication-events?${params}`

    let closedByEffect = false

    const connect = () => {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        if (pingRef.current) clearInterval(pingRef.current)
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping")
        }, 25000)
      }

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)

          // La grilla consolidada se alimenta de dos tipos de mensaje:
          // - "permanencia": DTO de visita (ingreso emparejado con egreso) -> upsert por visita.
          // - "authentication-event": solo se usa para no-identificados (sin permanencia),
          //   que el backend no publica como permanencia. Se mapea a una fila E-<id>.
          let dto = null
          if (payload?.type === "permanencia") {
            dto = payload.data
          } else if (payload?.type === "authentication-event") {
            const ev = payload.data
            if (!ev?.id) return
            if (ev.documentNumber != null && ev.documentNumber !== "") return // identificado: llega como permanencia
            const esEgreso = (ev.inOut || "").toLowerCase() === "out"
            dto = {
              key: `E-${ev.id}`,
              tipoFila: "NO_IDENTIFICADO",
              authEventIngresoId: esEgreso ? null : ev.id,
              authEventEgresoId: esEgreso ? ev.id : null,
              documento: ev.documentNumber,
              tipoDocumento: ev.documentType,
              nombre: ev.nombre,
              imagePath: ev.imagePath,
              segmentationCode: ev.segmentationCode,
              segmentationDescription: ev.segmentationDescription,
              matches: ev.matches,
              corsight: ev.corsight,
              sucursal: ev.device?.sucursales?.descripcion,
              empresa: ev.device?.sucursales?.empresa?.descripcion,
              evento: ev.device?.eventos?.descripcion,
              tipoDispositivoIngreso: ev.device?.type,
              dispositivoIngreso: ev.serialNo,
              dispositivoEgreso: ev.serialNo,
              entrada: esEgreso ? "NA" : "REGISTRADO",
              salida: esEgreso ? "REGISTRADO" : "NA",
              // Sin identificacion no hubo consulta al validador (mismo criterio que el DTO).
              pasoIngreso: "NA",
              pasoEgreso: "NA",
              fechaHoraIngreso: esEgreso ? null : ev.dateTime,
              fechaHoraEgreso: esEgreso ? ev.dateTime : null,
              tagPermanencia: "NA",
              huellaIngreso: ev.fingerprint != null,
              ticketValidoIngreso: ev.ticketValido,
              responseDetailIngreso: ev.responseDetail,
              huellaEgreso: esEgreso ? ev.fingerprint != null : null,
              ticketValidoEgreso: esEgreso ? ev.ticketValido : null,
              responseDetailEgreso: esEgreso ? ev.responseDetail : null,
            }
          } else {
            return
          }
          if (!dto?.key) return

          const rowKey = dto.key
          const wasKnown = rowsIdsRef.current.has(rowKey)

          let becameNew = false
          setRows((prevRows) => {
            const { rows: next, isNew } = upsertConsolidadoRow(prevRows, dto, !wasKnown)
            becameNew = isNew
            return next
          })

          if (!wasKnown) rowsIdsRef.current.add(rowKey)
          if (becameNew) setTotalCount((prev) => prev + 1)

          if (!wasKnown) {
            if (highlightTimeoutsRef.current[rowKey]) {
              clearTimeout(highlightTimeoutsRef.current[rowKey])
            }
            highlightTimeoutsRef.current[rowKey] = setTimeout(() => {
              setRows((prevRows) =>
                prevRows.map((row) => (row.id === rowKey ? { ...row, __highlight: false } : row))
              )
              delete highlightTimeoutsRef.current[rowKey]
            }, 2500)
          }
        } catch (error) {
          console.error("Error al procesar evento de autenticacion:", error)
        }
      }

      ws.onerror = (error) => {
        console.error("Error en websocket de eventos de autenticacion:", error)
      }

      ws.onclose = (evt) => {
        if (pingRef.current) {
          clearInterval(pingRef.current)
          pingRef.current = null
        }

        if (!closedByEffect && (evt.code === 1006 || evt.code === 1011)) {
          reconnectRef.current = setTimeout(connect, 2000)
        }
      }
    }

    connect()

    return () => {
      closedByEffect = true
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      if (pingRef.current) clearInterval(pingRef.current)
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close()
      }
    }
  }, [token, authExpired, wsEnabled, appliedWsFilters])

  const resetValues = () => {
    setNombreTerm("")
    setDocNro("")
    setIpEquipo("")
    setFechaDesde(null)
    setFechaHasta(null)
    setSelectedEmpresas("")
    setSelectedSucursales([])
    setSelectedEventos("")
    setSelectedTipoAcceso("")
    setSelectedZonas("")
    setNombreDispositivo("")
    setSelectedTipoDoc("")
    setNoIdentificados(null)
    setAccesosCorrectos(null)
    setDuplicados(null)
    setMultiplesCoincidencias(null)
    setSelectedSegmentos([])
  }

  const formatDate = (date) => {
    if (!date) return ""
    const pad = (n) => (n < 10 ? "0" + n : n)
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      " " +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds())
    )
  }

  const handleSearch = async () => {
    setAppliedWsFilters({
      sucursalIds: selectedSucursales && selectedSucursales.length > 0 ? selectedSucursales : undefined,
      eventoId: selectedEventos || undefined,
      zonaId: selectedZonas || undefined,
      empresaId: selectedEmpresas || undefined,
      nombre: nombreTerm || undefined,
      tipoDoc: selectedTipoDoc || undefined,
      documentNumber: docNro || undefined,
      tipoAcceso: selectedTipoAcceso || undefined,
      noIdentificados: (noIdentificados !== null && noIdentificados !== "") ? noIdentificados : undefined,
      multiplesCoincidencias: (multiplesCoincidencias !== null && multiplesCoincidencias !== "") ? multiplesCoincidencias : undefined,
      accesosCorrectos: (accesosCorrectos !== null && accesosCorrectos !== "") ? accesosCorrectos : undefined,
      segmentos: selectedSegmentos && selectedSegmentos.length > 0 ? selectedSegmentos : undefined,
    })
    try {
      setIsLoadingData(true)
      setNextPage(0)
      await fetchPageData(0, { append: false })
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleClearFilters = async () => {
    setIsLoadingData(true)
    try {
      setNextPage(0)
      flushSync(() => {
        resetValues()
      })
      setAppliedWsFilters({})
      await fetchPageData(0, { append: false })
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingData(false)
    }
  }

  const openNameDialog = (format) => {
    setPendingFormat(format)
    setExportFormat(format)
    setFileName("")
    setNameDialogOpen(true)
  }

  const closeNameDialog = () => setNameDialogOpen(false)

  const confirmDownload = async () => {
    setNameDialogOpen(false)
    await handleDownload(pendingFormat, fileName)
  }

  const handleDownload = async (format = exportFormat, nombre = "") => {
    setIsDownloading(true)
    closeDownloadMenu()
    let apiUrl = `${config.api.baseURL}/event/authentication-consolidado/export2/${format}?`

    if (selectedEmpresas) apiUrl += `empresaId=${encodeURIComponent(selectedEmpresas)}&`
    if (selectedSucursales && selectedSucursales.length > 0) {
      selectedSucursales.forEach((id) => {
        apiUrl += `sucursalIds=${encodeURIComponent(id)}&`
      })
    }
    if (selectedEventos) apiUrl += `eventoId=${encodeURIComponent(selectedEventos)}&`
    if (selectedZonas) apiUrl += `zonaId=${encodeURIComponent(selectedZonas)}&`
    if (nombreTerm) apiUrl += `nombre=${encodeURIComponent(nombreTerm)}&`
    if (selectedTipoDoc) apiUrl += `tipoDoc=${encodeURIComponent(selectedTipoDoc)}&`
    if (docNro) apiUrl += `document_number=${encodeURIComponent(docNro)}&`
    if (ipEquipo) apiUrl += `ipDevice=${encodeURIComponent(ipEquipo)}&`
    if (selectedTipoAcceso) apiUrl += `tipoAcceso=${encodeURIComponent(selectedTipoAcceso)}&`
    if (fechaDesde) apiUrl += `fechaDesde=${encodeURIComponent(formatDate(fechaDesde))}&`
    if (fechaHasta) apiUrl += `fechaHasta=${encodeURIComponent(formatDate(fechaHasta))}&`
    if (nombreDispositivo) apiUrl += `nombreDispositivo=${encodeURIComponent(nombreDispositivo)}&`
    if (noIdentificados !== null) apiUrl += `noIdentificados=${encodeURIComponent(noIdentificados)}&`
    if (duplicados !== null) apiUrl += `duplicados=${encodeURIComponent(duplicados)}&`
    if (accesosCorrectos !== null) apiUrl += `accesosCorrectos=${encodeURIComponent(accesosCorrectos)}&`
    if (multiplesCoincidencias !== null) apiUrl += `multiplesCoincidencias=${encodeURIComponent(multiplesCoincidencias)}&`
    if (selectedSegmentos && selectedSegmentos.length > 0) {
      selectedSegmentos.forEach((s) => {
        apiUrl += `segmentos=${encodeURIComponent(s)}&`
      })
    }
    if (totalPages !== null) apiUrl += `total=${encodeURIComponent(Math.ceil(totalPages * PAGE_SIZE / 100000))}&`

    if (nombre && nombre.trim().length > 0) {
      apiUrl += `nombreArchivo=${encodeURIComponent(nombre.trim())}&`
    }

    try {
      const res = await fetch(apiUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error("Error al descargar el archivo.")
      const data = await res.json()
      showToast({ icon: "info", title: "Mensaje", text: data.mensaje || "Respuesta recibida." })
    } catch (error) {
      console.error("Error al descargar el archivo:", error)
    } finally {
      setIsDownloading(false)
    }
  }

  const fetchPageData = async (page, { append = false } = {}) => {
    try {
      let apiUrl = `${config.api.baseURL}/event/authentication-consolidado?page=${page}&size=${PAGE_SIZE}&`

      if (selectedEmpresas) apiUrl += `empresaId=${encodeURIComponent(selectedEmpresas)}&`
      if (selectedSucursales && selectedSucursales.length > 0) {
        selectedSucursales.forEach((id) => {
          apiUrl += `sucursalIds=${encodeURIComponent(id)}&`
        })
      }
      if (selectedEventos) apiUrl += `eventoId=${encodeURIComponent(selectedEventos)}&`
      if (selectedZonas) apiUrl += `zonaId=${encodeURIComponent(selectedZonas)}&`
      if (nombreTerm) apiUrl += `nombre=${encodeURIComponent(nombreTerm)}&`
      if (selectedTipoDoc) apiUrl += `tipoDoc=${encodeURIComponent(selectedTipoDoc)}&`
      if (docNro) apiUrl += `document_number=${encodeURIComponent(docNro)}&`
      if (ipEquipo) apiUrl += `ipDevice=${encodeURIComponent(ipEquipo)}&`
      if (fechaDesde) apiUrl += `fechaDesde=${encodeURIComponent(formatDate(fechaDesde))}&`
      if (fechaHasta) apiUrl += `fechaHasta=${encodeURIComponent(formatDate(fechaHasta))}&`
      if (nombreDispositivo) apiUrl += `nombreDispositivo=${encodeURIComponent(nombreDispositivo)}&`
      if (noIdentificados !== null) apiUrl += `noIdentificados=${encodeURIComponent(noIdentificados)}&`
      if (duplicados !== null) apiUrl += `duplicados=${encodeURIComponent(duplicados)}&`
      if (accesosCorrectos !== null) apiUrl += `accesosCorrectos=${encodeURIComponent(accesosCorrectos)}&`
        if (multiplesCoincidencias !== null) apiUrl += `multiplesCoincidencias=${encodeURIComponent(multiplesCoincidencias)}&`
      if (selectedSegmentos && selectedSegmentos.length > 0) {
        selectedSegmentos.forEach((s) => {
          apiUrl += `segmentos=${encodeURIComponent(s)}&`
        })
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 200) {
        const result = await response.json()
        const totalPagesFromApi = result?.page?.totalPages ?? 0
        const totalElementsFromApi =
          result?.page?.totalElements ?? totalPagesFromApi * PAGE_SIZE
        const incomingRows = result?._embedded?.permanenciaGridDTOList
          ? mapConsolidadoToRows(result._embedded.permanenciaGridDTOList, config, page)
          : []

        setTotalPages(totalPagesFromApi)
        setTotalCount(totalElementsFromApi)
        setIdentifiedCounts({
          identificados: result?.identificados ?? 0,
          noIdentificados: result?.noIdentificados ?? 0,
        })
        setHasMore(page + 1 < totalPagesFromApi)
        setNextPage(page + 1)

        if (append) {
          setRows((prevRows) => mergeUniqueRows(prevRows, incomingRows))
        } else {
          setRows(reindexRows(incomingRows))
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const handleLoadMore = async () => {
    if (isLoadingData || !hasMore) return
    try {
      setIsLoadingData(true)
      await fetchPageData(nextPage, { append: true })
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleNombreSearch = (event) => setNombreTerm(event.target.value)
  const handleNombreDispositivoSearch = (event) => setNombreDispositivo(event.target.value)
  const handleNroDocSearch = (event) => setDocNro(event.target.value)
  const handleIpEquipoSearch = (event) => setIpEquipo(event.target.value)

  const getEmpresas = async () => {
    try {
      let empUrl = `${config.api.baseURL}/empresas`
      const response = await fetch(empUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 200) {
        const result = await response.json()
        if (result) {
          setEmpresas(result)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchSucursales = async (selectedId) => {
    try {
      if (selectedId) {
        let sucuUrl = `${config.api.baseURL}/sucursales/empresa/${selectedId}`
        const response = await fetch(sucuUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.status === 200) {
          const result = await response.json()
          setSelectedSucursales([])
          setZonas([])
          setSelectedZonas("")
          if (result) {
            setSucursales(result)
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchEventos = async (selectedId) => {
    try {
      if (selectedId) {
        let eveUrl = `${config.api.baseURL}/eventos/empresa/${selectedId}`
        const response = await fetch(eveUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.status === 200) {
          const result = await response.json()
          setSelectedEventos("")
          if (result) {
            setEventos(result)
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchZonas = async (selectedIds) => {
    try {
      const ids = Array.isArray(selectedIds) ? selectedIds : (selectedIds ? [selectedIds] : [])
      if (ids.length === 0) {
        setSelectedZonas("")
        setZonas([])
        return
      }
      const zonaUrl = `${config.api.baseURL}/zonas/sucursales?ids=${ids.join(",")}`
      const response = await fetch(zonaUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 200) {
        const result = await response.json()
        setSelectedZonas("")
        if (result) {
          setZonas(result)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const handleEmpresasChange = (e) => {
    const selectedId = e.target.value
    setSelectedEmpresas(selectedId)
    fetchSucursales(selectedId)
    fetchEventos(selectedId)
  }

  const handleEventosChange = (e) => {
    const selectedId = e.target.value
    setSelectedEventos(selectedId)
  }

  const controlsDisabled = isLoadingData || isDownloading

  if (authExpired) return null

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDBox display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <MDButton
                variant="outlined"
                color="success"
                size="small"
                onClick={toggleFilters}
              >
                {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
              </MDButton>
              {canUseWs && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={wsEnabled}
                      onChange={(e) => {
                        setWsEnabled(e.target.checked)
                        localStorage.setItem("wsEnabled_ingresos", e.target.checked)
                      }}
                      color="info"
                    />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Datos en tiempo real{wsEnabled ? " (activo)" : ""}
                    </MDTypography>
                  }
                />
              )}
            </MDBox>

            <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
              <Card sx={{ p: 2, mb: 3 }}>
                <MDTypography variant="h6" color="text" gutterBottom>
                  Filtros
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Empresa</InputLabel>
                      <Select
                        label="Empresa"
                        variant="outlined"
                        fullWidth
                        value={selectedEmpresas}
                        onChange={handleEmpresasChange}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Ninguna</MenuItem>
                        {empresas.map((emp) => (
                          <MenuItem key={emp.idEmpresa} value={emp.idEmpresa}>
                            {emp.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ModalSelectorMultiple
                      label="Sucursal"
                      value={selectedSucursales}
                      onChange={(selectedIds) => {
                        setSelectedSucursales(selectedIds)
                        fetchZonas(selectedIds)
                      }}
                      items={sucursales || []}
                      itemValue="idSucursal"
                      itemLabel="descripcion"
                      placeholder="Seleccione.."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Evento</InputLabel>
                      <Select
                        label="Evento"
                        variant="outlined"
                        fullWidth
                        value={selectedEventos}
                        onChange={handleEventosChange}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Ninguna</MenuItem>
                        {eventos?.map((event) => (
                          <MenuItem key={event.idEvento} value={event.idEvento}>
                            {event.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Zonas</InputLabel>
                      <Select
                        label="Zona"
                        variant="outlined"
                        fullWidth
                        value={selectedZonas}
                        onChange={(e) => setSelectedZonas(e.target.value)}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Ninguna</MenuItem>
                        {zonas?.map((z) => (
                          <MenuItem key={z.idZona} value={z.idZona}>
                            {z.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Nombre/Apellido"
                      variant="outlined"
                      fullWidth
                      value={nombreTerm}
                      onChange={handleNombreSearch}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") handleNombreSearch(event)
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Documento</InputLabel>
                      <Select
                        label="Tipo de Documento"
                        variant="outlined"
                        fullWidth
                        value={selectedTipoDoc}
                        onChange={(e) => setSelectedTipoDoc(e.target.value)}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Ninguno</MenuItem>
                        <MenuItem key="CIP" value="CIP">CIP</MenuItem>
                        <MenuItem key="DNI" value="DNI">DNI</MenuItem>
                        <MenuItem key="PAS" value="PAS">PAS</MenuItem>
                        <MenuItem key="CPF" value="CPF">CPF</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Nro. de Documento"
                      variant="outlined"
                      fullWidth
                      value={docNro}
                      onChange={handleNroDocSearch}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") handleNroDocSearch(event)
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="IP equipo"
                      variant="outlined"
                      fullWidth
                      value={ipEquipo}
                      onChange={handleIpEquipoSearch}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Nombre de dispositivo"
                      variant="outlined"
                      fullWidth
                      value={nombreDispositivo}
                      onChange={handleNombreDispositivoSearch}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") handleNombreDispositivoSearch(event)
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Acceso</InputLabel>
                      <Select
                        label="Tipo de Acceso"
                        variant="outlined"
                        fullWidth
                        value={selectedTipoAcceso}
                        onChange={(e) => setSelectedTipoAcceso(e.target.value)}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem key="in" value="in">Entrada</MenuItem>
                        <MenuItem key="out" value="out">Salida</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de identificación</InputLabel>
                      <Select
                        label="Tipo de identificación"
                        variant="outlined"
                        fullWidth
                        value={noIdentificados ?? ""}
                        onChange={(e) => {
                          const value = e.target.value === "" ? null : e.target.value
                          setNoIdentificados(value)
                          if (value !== false) {
                            setDuplicados(null)
                          }
                        }}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem key="0" value={false}>Identificados</MenuItem>
                        <MenuItem key="1" value={true}>No identificados</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Tooltip
                      title='Para filtrar por este campo debe excluir los "No Identificados"'
                      disableHoverListener={noIdentificados === false}
                      disableFocusListener={noIdentificados === false}
                      disableTouchListener={noIdentificados === false}
                    >
                      <span style={{ display: "block" }}>
                        <FormControl fullWidth sx={darkMode ? {
                          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.23)" },
                          "& .MuiInputLabel-root.Mui-disabled": { color: "rgba(255,255,255,0.38)" },
                        } : {}}>
                          <InputLabel>Duplicados</InputLabel>
                          <Select
                            label="Duplicados"
                            variant="outlined"
                            fullWidth
                            value={duplicados ?? ""}
                            onChange={(e) => setDuplicados(e.target.value)}
                            disabled={noIdentificados !== false}
                            sx={{ p: 1.5 }}
                            placeholder="Seleccione.."
                          >
                            <MenuItem value="">Default</MenuItem>
                            <MenuItem key="1" value={true}>Incluir</MenuItem>
                            <MenuItem key="0" value={false}>Excluir</MenuItem>
                          </Select>
                        </FormControl>
                      </span>
                    </Tooltip>
                  </Grid>
                  {/* Solo accesos correctos oculto */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>¿Multiples coincidencias?</InputLabel>
                      <Select
                        label="¿Multiples coincidencias?"
                        variant="outlined"
                        fullWidth
                        value={multiplesCoincidencias}
                        onChange={(e) => setMultiplesCoincidencias(e.target.value)}
                        sx={{ p: 1.5 }}
                        placeholder="Seleccione.."
                      >
                        <MenuItem value="">Default</MenuItem>
                        <MenuItem key="1" value={true}>Si</MenuItem>
                        <MenuItem key="0" value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Flatpickr
                      value={fechaDesde}
                      options={{
                        locale: "es",
                        enableTime: true,
                        time_24hr: true,
                        defaultHour: new Date().getHours(),
                        defaultMinute: new Date().getMinutes(),
                      }}
                      onChange={(date) => setFechaDesde(date[0])}
                      render={({ defaultValue, value, ...props }, ref) => (
                        <TextField fullWidth label="Fecha Desde" variant="outlined" value={formatDate(fechaDesde)} inputRef={ref} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Flatpickr
                      value={fechaHasta}
                      options={{
                        locale: "es",
                        enableTime: true,
                        time_24hr: true,
                        defaultHour: new Date().getHours(),
                        defaultMinute: new Date().getMinutes(),
                      }}
                      onChange={(date) => setFechaHasta(date[0])}
                      render={({ defaultValue, value, ...props }, ref) => (
                        <TextField fullWidth label="Fecha Hasta" variant="outlined" value={formatDate(fechaHasta)} inputRef={ref} />
                      )}
                    />
                  </Grid>
                  {canFilterSegmento && (
                    <Grid item xs={12} sm={6} md={4}>
                      <ModalSelectorMultiple
                        label="Segmento"
                        value={selectedSegmentos}
                        onChange={(values) => setSelectedSegmentos(values)}
                        items={SEGMENTO_OPTIONS}
                        itemValue="code"
                        itemLabel="descripcion"
                        placeholder="Seleccione.."
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={12}>
                    <MDButton color="info" variant="gradient" onClick={handleSearch} disabled={controlsDisabled}>
                      Buscar
                    </MDButton>
                    &nbsp;
                    <MDButton color="info" variant="outlined" onClick={handleClearFilters} disabled={controlsDisabled}>
                      Limpiar
                    </MDButton>
                    &nbsp;
                    {/* Botón de descargas con menú */}
                    <MDButton
                      color="success"
                      variant="outlined"
                      onClick={openDownloadMenu}
                      disabled={controlsDisabled}
                      endIcon={isDownloading ? <CircularProgress size={20} /> : <Icon>file_download</Icon>}
                    >
                      {isDownloading ? "Generando..." : "Descargar"}
                    </MDButton>
                    <Menu anchorEl={downloadAnchorEl} open={downloadMenuOpen} onClose={closeDownloadMenu}>
                      <MenuItem disabled={controlsDisabled} onClick={() => { closeDownloadMenu(); openNameDialog("XLSX") }}>Excel</MenuItem>
                      <MenuItem disabled={controlsDisabled} onClick={() => { closeDownloadMenu(); openNameDialog("PDF") }}>PDF</MenuItem>
                    </Menu>
                  </Grid>
                </Grid>
              </Card>
            </Collapse>

            <MDBox pt={3}>
              <Card>
                <MDBox px={3} pt={2} pb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <MDTypography variant="button" fontWeight="regular">
                      Total: {new Intl.NumberFormat("es-PY").format(totalCount)} registros
                    </MDTypography>
                    {identifiedCounts && (
                      <>
                        <MDTypography variant="button" fontWeight="regular" color="success">
                          Identificados: {new Intl.NumberFormat("es-PY").format(identifiedCounts.identificados ?? 0)}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="regular" color="warning">
                          No identificados: {new Intl.NumberFormat("es-PY").format(identifiedCounts.noIdentificados ?? 0)}
                        </MDTypography>
                      </>
                    )}
                  </MDBox>
                  <MDTypography variant="caption" color="text">
                    Mostrando {new Intl.NumberFormat("es-PY").format(rows.length)}
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{ columns, rows: rows }}
                  isSorted={false}
                  noEndBorder
                  showTotalEntries={false}
                  entriesPerPage={false}
                  canSearch={false}
                  pagination={false}
                />
                <MDBox pb={3} pt={3} px={3} display="flex" justifyContent="center">
                  {hasMore && (
                    <MDButton
                      color="info"
                      variant="outlined"
                      onClick={handleLoadMore}
                      disabled={controlsDisabled}
                    >
                      {isLoadingData ? "Cargando..." : "Cargar más"}
                    </MDButton>
                  )}
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal para ingresar nombre del archivo */}
      <Dialog PaperProps={{
        style: dialogStyle,
      }} open={nameDialogOpen} onClose={closeNameDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{
          borderBottom: darkMode ? "1px solid #2d2d2d" : "1px solid #e0e0e0",
          color: darkMode ? "white" : "dark",
        }}>Nombre del archivo ({pendingFormat})</DialogTitle>
        <DialogContent>
          <Tooltip title={FILE_NAME_HELP_TEXT} placement="top">
            <span style={{ display: "block" }}>
              <TextField
                autoFocus
                margin="dense"
                label="Nombre "
                type="text"
                fullWidth
                value={fileName}
                onChange={(e) => setFileName(sanitizeFileName(e.target.value))}
                placeholder={`reporte_${pendingFormat.toLowerCase()}`}
              />
            </span>
          </Tooltip>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={closeNameDialog} color="secondary" variant="outlined">Cancelar</MDButton>
          <MDButton onClick={confirmDownload} color="success" variant="gradient">Confirmar</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

export default Ingresos
